import { Button, Chip, IconButton, Tooltip } from '@mui/material';
import React, { useContext, useRef } from 'react';
import { useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { v4 as uuidv4 } from 'uuid';

// ContextAPI
import { FormData, PopupContext } from '../../CreateQuestionnaire';
import { getFormType } from '../../constants/formTypes.js';
import { USER_SIDE_TEMPLATE_TYPES } from '../../constants/templateTypes.js';

// Constants
import { POPUP_TYPES } from '../../constants/popupTypes';
import FormComponent from '../SectionComponent/SectionComponent';

// Icons
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PostAddRoundedIcon from '@mui/icons-material/PostAddRounded';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import AltRouteRoundedIcon from '@mui/icons-material/AltRouteRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

const SECTION_DRAG_TYPE = 'section';

// Styles
import styles from './GroupElement.module.css';
import GraphStructureService from '../../services/graph.structurer.service.js';
import Alert from '@mui/material/Alert';
// import { WarningAmberOutlined } from '@mui/icons-material';
import { TEMPLATE } from '../../constants/templateTypes.js';
import { CodeOutlined, GetApp } from '@mui/icons-material';
import { copyToClipBoard } from 'src/util/util';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import SnackBar from 'src/components/UI/snack/SnackBar';
import ConfirmDeleteDialog from '../ConfirmDeleteDialog';

function GroupElement({ groupId, parentId, index }) {
  const gs = new GraphStructureService();
  const [popupContext, setPopupContext] = useContext(PopupContext);
  const [formDataContext, setFormDataContext] = useContext(FormData);
  const [data] = useState(formDataContext.schema[groupId]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const sectionRef = useRef(null);

  const isReorderable = typeof parentId === 'string' && parentId.length > 0;

  // Handle Functions
  const handleAddGroup = (formType) => {
    setPopupContext({
      ...popupContext,
      show: true,
      type: POPUP_TYPES.ADD_GROUP,
      data,
      edit: false,
      formType: formType
    });
  };

  const handleEditSection = () => {
    setPopupContext({
      ...popupContext,
      show: true,
      type: POPUP_TYPES.ADD_GROUP,
      data,
      sectionData: data,
      edit: true
    });
  };

  const handleAddTemplate = (item) => {
    const updatedFormDataContext = { ...formDataContext };
    const templateName = USER_SIDE_TEMPLATE_TYPES[item.toolName];
    TEMPLATE[templateName].forEach((formElement) => {
      const updatedFormElement = { ...formElement, id: uuidv4() };
      updatedFormDataContext.schema[groupId].formElements.push(
        updatedFormElement
      );
    });
    setFormDataContext(updatedFormDataContext);
  };

  const handleAddFormElement = (item) => {
    if (USER_SIDE_TEMPLATE_TYPES[item.toolName]) {
      handleAddTemplate(item);
      return;
    }
    setPopupContext({
      show: true,
      type: POPUP_TYPES.ADD_FORM_ELEMENT,
      data: {
        id: groupId,
        type: getFormType(item.toolName)
      }
    });
  };

  const handleDeleteSection = () => {
    const updatedFormData = gs.removeSection(groupId, formDataContext);
    setFormDataContext({ ...updatedFormData });
  };

  const moveElement = (fromIndex, toIndex) => {
    setFormDataContext(
      gs.moveFormElement(groupId, fromIndex, toIndex, formDataContext)
    );
  };

  const moveSection = (fromIndex, toIndex) => {
    setFormDataContext(
      gs.moveSection(parentId, fromIndex, toIndex, formDataContext)
    );
  };

  const [{ hover }, drop] = useDrop({
    accept: 'tool',
    drop: (item) => {
      handleAddFormElement(item);
    },
    collect: (monitor) => ({
      hover: monitor.isOver({ shallow: true })
    })
  });

  const [, sectionDrop] = useDrop({
    accept: SECTION_DRAG_TYPE,
    hover(item, monitor) {
      if (!sectionRef.current || !isReorderable) return;
      if (item.parentId !== parentId) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;

      const rect = sectionRef.current.getBoundingClientRect();
      const middleY = (rect.bottom - rect.top) / 2;
      const clientY = monitor.getClientOffset().y - rect.top;
      if (dragIndex < hoverIndex && clientY < middleY) return;
      if (dragIndex > hoverIndex && clientY > middleY) return;

      moveSection(dragIndex, hoverIndex);
      item.index = hoverIndex;
    }
  });

  const [{ isSectionDragging }, sectionDrag] = useDrag({
    type: SECTION_DRAG_TYPE,
    item: () => ({ id: groupId, index, parentId }),
    canDrag: isReorderable,
    collect: (monitor) => ({ isSectionDragging: monitor.isDragging() })
  });

  drop(sectionDrop(sectionRef));

  if (data.formType === undefined || data.formType === null)
    data.formType = true;

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const copyConfig = () => {
    const elements = formDataContext.schema[groupId]?.formElements ?? [];
    copyToClipBoard(JSON.stringify(elements));
    setSnackbar({
      open: true,
      message: 'Configuration copied to clipboard',
      severity: 'success'
    });
  };

  const setConfig = (data) => {
    const formData = JSON.parse(data);
    const updatedFormDataContext = { ...formDataContext };
    formData.forEach((formElement) => {
      const updatedFormElement = { ...formElement, id: uuidv4() };
      updatedFormDataContext.schema[groupId].formElements.push(
        updatedFormElement
      );
    });
    setFormDataContext(updatedFormDataContext);
  };

  const loadConfig = () => {
    setPopupContext({
      show: true,
      type: POPUP_TYPES.LOAD_CONFIG,
      data: {
        id: groupId,
        setConfig
      }
    });
  };

  const t = useAutoTranslation();

  const canDelete = formDataContext.schema[groupId].previousConnections.filter(
    (n) => n
  ).length;
  const elementCount = data.formElements.length;

  const isForm = data.formType;
  const isRoot = !canDelete;
  const cond = data.condition;
  const hasCondition =
    !isRoot &&
    cond &&
    cond.condition &&
    cond.condition !== 'None' &&
    cond.type !== 'NONE';
  const conditionValue = Array.isArray(cond?.value)
    ? cond.value.join(', ')
    : cond?.value;

  return (
    <div
      id={groupId}
      className={`${styles.GroupElement} ${
        isForm ? styles.FormCard : styles.SectionCard
      } ${hover ? styles.GroupElementHover : ''} ${
        isSectionDragging ? styles.SectionDragging : ''
      }`}
      ref={sectionRef}
    >
      <SnackBar
        open={snackbar.open}
        setOpen={(open) => setSnackbar((s) => ({ ...s, open }))}
        message={snackbar.message}
        severity={snackbar.severity}
      />
      <ConfirmDeleteDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDeleteSection}
        title={data.formType ? t('Delete form') : t('Delete section')}
        message={
          data.formType
            ? t(
                'Are you sure you want to delete this form? Any sub-sections and conditions that depend on it will also be removed.'
              )
            : t(
                'Are you sure you want to delete this section? Any sub-sections and conditions that depend on it will also be removed.'
              )
        }
        itemName={data.title}
      />
      <div className={styles.SectionHeader}>
        {isReorderable && (
          <Tooltip
            title={
              isForm ? t('Drag to reorder form') : t('Drag to reorder section')
            }
            arrow
          >
            <span ref={sectionDrag} className={styles.SectionDragHandle}>
              <DragIndicatorIcon fontSize="small" />
            </span>
          </Tooltip>
        )}
        <div className={styles.SectionTitleWrap}>
          <div className={styles.BadgeRow}>
            <Chip
              size="small"
              icon={isForm ? <ArticleOutlinedIcon /> : <FolderOutlinedIcon />}
              label={isForm ? t('Form') : t('Section')}
              className={isForm ? styles.FormBadge : styles.SectionBadge}
            />
            {/* <span className={styles.VisibilityHint}>
              {isForm ? t('Visible to patient') : t('Hidden grouping')}
              {isRoot ? ` · ${t('Entry point')}` : ''}
            </span> */}
          </div>
          <h2 className={styles.SectionTitle}>
            {data.title ||
              (isForm ? t('Untitled form') : t('Untitled section'))}
          </h2>
          {data.description && (
            <p className={styles.SectionDescription}>{data.description}</p>
          )}
        </div>
        <div className={styles.SectionActions}>
          <Tooltip title={isForm ? t('Edit form') : t('Edit section')} arrow>
            <IconButton size="small" onClick={handleEditSection}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canDelete ? (
            <Tooltip
              title={isForm ? t('Delete form') : t('Delete section')}
              arrow
            >
              <IconButton size="small" onClick={() => setConfirmOpen(true)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <></>
          )}
          <Tooltip title={t('Copy configuration')} arrow>
            <IconButton size="small" onClick={copyConfig}>
              <GetApp fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('Load configuration')} arrow>
            <IconButton size="small" onClick={loadConfig}>
              <CodeOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      </div>
      {!isRoot &&
        (hasCondition ? (
          <div className={styles.ConditionBanner}>
            <AltRouteRoundedIcon
              fontSize="small"
              className={styles.ConditionIcon}
            />
            <span>
              {t('Shows when')} <strong>{cond.condition}</strong>
              {conditionValue ? (
                <>
                  {' '}
                  {t('is')} <strong>{conditionValue}</strong>
                </>
              ) : null}
            </span>
          </div>
        ) : (
          <div className={styles.ConditionMuted}>
            <VisibilityOutlinedIcon fontSize="small" />
            <span>{t('Always shown')}</span>
          </div>
        ))}
      {data.warning && (
        <Alert
          // icon={<WarningAmberOutlined fontSize="inherit" />}
          severity="error"
        >
          {data.warning}
        </Alert>
      )}
      <div className={styles.ElementsWrap}>
        {elementCount ? (
          data.formElements.map((element, index) => {
            return (
              <FormComponent
                key={element.id || `FormElement_${index}`}
                element={element}
                groupId={groupId}
                index={index}
                moveElement={moveElement}
              />
            );
          })
        ) : (
          <div
            className={`${styles.DropZone} ${
              hover ? styles.DropZoneActive : ''
            }`}
          >
            <PostAddRoundedIcon fontSize="small" />
            <span>{t('Drag a tool here to add a question')}</span>
          </div>
        )}
      </div>
      <div className={styles.SectionFooter}>
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => {
            handleAddGroup(false);
          }}
        >
          {t(`ADD SECTION`)}
        </Button>

        {data.formType && (
          <Button
            variant="contained"
            color="secondary"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              handleAddGroup(true);
            }}
          >
            {t(`ADD FORM`)}
          </Button>
        )}
      </div>
    </div>
  );
}

export default GroupElement;
